import units from '@/sui/data/units'
import microOrgs from '@/sui/data/microOrgs'
import go from '@/gojs/go'
import regions from '@/sui/tools/regions'

import {
  validDate,
  newDate,
  getFields,
  duration,
  strIndex
} from '@/sui/data/flatten/tools'
import moment from 'moment'

export function unitType (y, u) {
  if (u == null) {
    return null
  }
  return units[y].findFromIndex(u).code
}

export function patientAge (r, y) {
  if (r.entry == null || r.birthdate == null) {
    return null
  }
  const fd = moment(`${y}-01-01`)
  const entry = moment(r.entry)
  const latter = entry.isAfter(fd) ? entry : fd

  return latter.diff(moment(r.birthdate), 'years')
}

export function patientDays (r, y) {
  if (r.birthWeight == null || r.entry == null || r.birthdate == null) {
    return null
  }

  const fd = moment(`01/01/${y}`)
  const entry = moment(r.entry)
  const latter = entry.isAfter(fd) ? entry : fd

  return latter.diff(moment(r.birthdate), 'days')
}

export function episodAge (r, i) {
  if (i.episod == null || r.birthdate == null) {
    return null
  }

  return moment(i.episod).diff(moment(r.birthdate), 'years')
}

export function episodDays (r, i) {
  if (r.birthWeight == null || i.episod == null || r.birthdate == null) {
    return null
  }

  return moment(i.episod).diff(moment(r.birthdate), 'days')
}

export function origin (o) {
  if (o == null) {
    return null
  }

  const value = parseInt(o)
  const t = go.fields2019.origin.find(i => i.code === value).value
  return t.substring(0, t.indexOf(' '))
}

export function neoOrigin (o) {
  if (o == null) {
    return null
  }

  const value = parseInt(o)
  const t = go.fields2019.neoOrigin.find(i => i.code === value).value
  return t.substring(0, t.indexOf(' '))
}

export function moCode (mo) {
  if (mo == null) {
    return null
  }
  const value = parseInt(mo)
  return microOrgs.completeList.v2.find(i => i.index === value).code
}

export function bmrMO (mo) {
  if (mo == null) {
    return null
  }
  const value = parseInt(mo)
  return microOrgs.bmrBhre.v1.find(i => i.code === value).mo
}

export function addBMRBHreMO (prefix, target, parent, mo) {
  if (mo.code == null) {
    return
  }

  const config = [ { key: 'type', get: c => c.code } ]
  getFields(config, prefix, target, mo)
}

function addResistance (prefix, target, a, version) {
  const res = microOrgs.resistances[version].find(i => i.index === a.code)
  const value = res.values.find(i => i.index === a.resistance)
  const index = strIndex(res.index)
  target[`${prefix}_res_${index}_${res.field}`] = value.code
}

export function addMO (prefix, target, mo, explaur, version) {
  const config = [
    { key: 'germ', get: m => moCode(m.code) },
    { key: 'microOrgDetail', get: m => m.microOrgDetail }
  ]

  if (explaur) {
    config.push(
      { key: 'explaur_id', get: m => m.explaurID }
    )
  }
  getFields(config, prefix, target, mo)
  mo.antibio.forEach(
    a => addResistance(prefix, target, a, version)
  )
}

export function divDuration (p, initial, finals) {
  const f = initial(p)
  if (validDate(f) == null) {
    return null
  }
  for (let i in finals) {
    const l = finals[i]
    const s = l(p)
    if (validDate(s) == null) {
      continue
    }
    return duration(s, f)
  }

  return null
}

export function ktDuration (parent, target, removal, insertion) {
  if (target.insertion == null) {
    return null
  }

  const targets = [target.removal, parent.exit, parent.death]
  for (let i in targets) {
    const l = targets[i]
    if (newDate(l) != null) {
      return duration(l, target.insertion)
    }
  }
}

export function addBacteremia (prefix, target, parent, bact, v) {
  const config = [
    { key: 'episod', get: b => newDate(b.episod) }
  ]
  if (v >= 2) {
    config.push(
      { key: 'diag_date', get: b => newDate(b.diagDate) },
      { key: 'origin', get: b => b.origin },
      { key: 'covid', get: b => b.covidStatus }
    )
    if (v >= 5) {
      config.push({ key: 'episod_immuno', get: b => b.episodImmuno })
    }
    config.push(
      { key: 'entrance', get: b => b.entrance_v2 },
      { key: 'a13_kt_type', get: b => b.catheterType_v2 }
    )
  } else {
    config.push(
      { key: 'origin', get: b => b.origin },
      { key: 'entrance', get: b => b.entrance },
      { key: 'entrance24_kt_type', get: b => b.catheterType }
    )
  }

  config.push(
    { key: 'kt_insertion', get: b => newDate(b.insertion) }
  )

  if (v >= 6) {
    config.push(
      {
        key: 'kt_tunneled',
        get: b => b.tunneled != null ? b.tunneled : b.enfTunneled
      }
    )
  }

  if (v >= 3) {
    config.push(
      {
        key: 'manip',
        get: b => b.previousManip != null
          ? b.previousManip : b.enfPreviousManip
      },
      {
        key: 'manip_type',
        get: b => b.previousManipType != null
          ? b.previousManipType : b.enfPreviousManipType
      },
      {
        key: 'manip_date',
        get: (b) => {
          const p = newDate(b.previousManipDate)
          return p != null ? p : newDate(b.enfPreviousManipDate)
        }
      }
    )
  }

  config.push(
    { key: 'kt_insertion_site', get: b => b.insertionSite },
    { key: 'kt_removed', get: b => b.removed },
    { key: 'kt_removal', get: b => newDate(b.removal) },
    { key: 'kt_duration', get: b => ktDuration(parent, b) }
  )

  if (v >= 2) {
    config.push(
      { key: 'kt_criteria', get: b => b.divDiag }
    )

    if (v === 2) {
      config.push(
        { key: 'kt_neo_criteria', get: b => b.neoDivDiag }
      )
    } else if (v >= 3) {
      config.push(
        { key: 'clinical_signs', get: b => b.clinicalSigns },
        { key: 'bio_signs', get: b => b.bioSigns },
        { key: 'adapted_antibio', get: b => b.adaptedAntibio },
        { key: 'two_hemocs', get: b => b.twoHemocs }
      )
    }

    config.push(
      { key: 'kt_lipids', get: b => b.lipids }
    )

    if (v === 2) {
      config.push({ key: 'a4_urinary', get: b => b.urinary })
    } else if (v > 2) {
      config.push({ key: 'a4_urinary', get: b => b.urinary_v2 })
    }

    config.push(
      { key: 'a4_urinary_start', get: b => newDate(b.urinaryStart) },
      { key: 'a4_urinary_removed', get: b => b.urinaryRemoved },
      { key: 'a4_urinary_end', get: b => newDate(b.urinaryEnd) },
      { key: 'a4_diag', get: b => b.urinaryDiag },
      { key: 'a3_intubated', get: b => b.bactintubated },
      { key: 'a3_intubation_start', get: b => newDate(b.bactintubStart) }
    )

    if (v >= 3) {
      config.push(
        { key: 'a3_reintubated', get: b => b.bactreintubated },
        { key: 'a3_intubation_end', get: b => newDate(b.bactintubEnd) }
      )
    }
  }
  getFields(config, prefix, target, bact)
  bact.microOrganisms.forEach(
    (mo, i) => addMO(`${prefix}_99_mo${strIndex(i + 1)}`, target, mo, v >= 3, v >= 4 ? 'v2' : 'v1')
  )
}

export function addPneumopathy (prefix, target, parent, pneu, v, origin) {
  const config = [
    { key: 'episod', get: c => newDate(c.episod) },
    { key: 'diagnosis', get: c => c.diagnosis }
  ]

  if (v >= 2) {
    config.push(
      { key: 'diag_date', get: c => newDate(c.diagDate) },
      { key: 'covid', get: c => c.covidStatus }
    )
  }

  if (origin) {
    config.push(
      { key: 'origin', get: c => c.origin }
    )
  }

  getFields(config, prefix, target, pneu)
  pneu.microOrganisms.forEach(
    (mo, i) => addMO(`${prefix}_99_mo${strIndex(i + 1)}`, target, mo, false, v >= 3 ? 'v2' : 'v1')
  )
}

export function findSurveillance (result, p) {
  const s = result.p.surveillances
  if (s == null) {
    return null
  }
  if (s[result.si].id === p.sv && result.cache.s != null) {
    return result.cache.s
  }
  while (s[result.si].id !== p.sv) {
    ++result.si
    if (result.si === s.length) {
      throw new Error(
        `Invalid surveillance index ${result.si}: ${p.sv} ${s[0]}`
      )
    }
  }

  const surv = s[result.si]
  const out = {
    status: surv.status,
    year: surv.year,
    etb_finess: surv.establishment.finess,
    etb_region: region(surv.establishment.finess),
    etb_name: surv.establishment.name,
    etb_type: surv.establishment.type
  }

  const d = result.p.denominators
  if (d != null && result.di < d.length && d[result.di] != null) {
    while (result.di < d.length && d[result.di].sv < p.sv) {
      ++result.di
    }

    if (result.di < d.length) {
      const denom = d[result.di]
      if (denom.sv === p.sv) {
        out.etb_status = etbStatus(denom.f.es)
        out.etb_type = etbType(denom.f.et)
      }
    }
  }
  result.cache.s = out
  return out
}

export function initializeSurveillance (result, s) {
  if (s != null) {
    result.etb_finess = s.etb_finess
    result.etb_region = s.etb_region
    result.etb_name = s.etb_name
    result.etb_status = s.etb_status
    result.etb_type = s.etb_type
  }
}

export function sheetStatus (s) {
  if (s === 'valid') {
    return 'Validée'
  }

  if (s === 'open') {
    return 'En cours de saisie'
  }

  return null
}

export function etbStatus (i) {
  if (i == null || i === 0) {
    return null
  }
  const status = ['public', 'espic', 'privé']
  return status[i - 1]
}

export function etbType (i) {
  if (i == null || i <= 0) {
    return null
  }

  const types = [
    {
      'code': 1,
      'short': 'CHRU/CHR',
      'value': 'CHRU/CHR - Centre Hospitalier Régional'
    },
    {
      'code': 2,
      'short': 'CH',
      'value': 'CH - Centre Hospitalier'
    },
    {
      'code': 3,
      'short': 'HL',
      'value': 'CH (ex-HL) - Centre Hospitalier, ex-hopital local'
    },
    {
      'code': 4,
      'short': 'HA',
      'value': 'HA - Établissement de santé relevant du service de santé des armées'
    },
    {
      'code': 5,
      'short': 'CLCC',
      'value': 'CLCC - Centre de Lutte Contre le Cancer'
    },
    {
      'code': 6,
      'short': 'MCO',
      'value': 'Clinique MCO - Établissement de Soins de Courte Durée'
    },
    {
      'code': 7,
      'short': 'SSR',
      'value': 'SSR - Établissement de santé privé autorisés en SSR'
    },
    {
      'code': 8,
      'short': 'SLD',
      'value': 'SLD - Établissement de Soins de Longue Durée'
    },
    {
      'code': 9,
      'short': 'PSY',
      'value': 'PSY - Centre Hospitalier spécialisé dans la Lutte contre les Maladies Mentales'
    },
    {
      'code': 10,
      'short': 'HAD',
      'value': 'HAD - Hospitalisation à domicile'
    },
    {
      'code': 11,
      'short': 'DIA',
      'value': 'DIA - Centre de dialyse'
    },
    {
      'code': 12,
      'short': 'EHP',
      'value': 'EHP - EHPAD indépendant'
    }
  ]
  return types.find(f => f.code === i).short
}

export function region (f) {
  for (let k in regions.regions) {
    const r = regions.regions[k]
    const fcount = r.finess.length
    for (let j = 0; j !== fcount; ++j) {
      if (f.startsWith(r.finess[j])) {
        return r.name
      }
    }
  }
  throw new Error('Finess not found in any region')
}
